import { useState } from "react";
import { usePortfolioReport, useRepaymentReport, useOverdueReport, useDisbursementReport, useInterestIncomeReport, useApprovalAuditReport, useCollectionPerformanceReport, useMemberStatement } from "@/hooks/useLoans";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import {
  Loader, Download, Printer, BarChart3, FileText, AlertTriangle,
  TrendingUp, DollarSign, Clock, CheckCircle2, Calendar,
} from "lucide-react";
import { format } from "date-fns";
import { formatCurrency, cn } from "@/lib/utils";
import { toast } from "sonner";

export default function LoanReports() {
  const [activeReport, setActiveReport] = useState("portfolio");
  const now = new Date();
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [year, setYear] = useState(now.getFullYear());
  const [memberSearch, setMemberSearch] = useState("");

  const { data: portfolioData, isLoading: loadingPortfolio } = usePortfolioReport();
  const { data: repaymentData, isLoading: loadingRepayment } = useRepaymentReport(month, year);
  const { data: overdueData, isLoading: loadingOverdue } = useOverdueReport();
  const { data: disbursementData, isLoading: loadingDisbursement } = useDisbursementReport(month, year);
  const { data: interestData, isLoading: loadingInterest } = useInterestIncomeReport();
  const { data: approvalData, isLoading: loadingApproval } = useApprovalAuditReport();
  const { data: collectionData, isLoading: loadingCollection } = useCollectionPerformanceReport();
  const { data: statementData, isLoading: loadingStatement } = useMemberStatement(memberSearch);

  const reports = [
    { id: "portfolio", label: "Portfolio", icon: <BarChart3 className="h-4 w-4" /> },
    { id: "repayments", label: "Monthly Repayments", icon: <Calendar className="h-4 w-4" /> },
    { id: "overdue", label: "Overdue", icon: <AlertTriangle className="h-4 w-4" /> },
    { id: "disbursement", label: "Disbursement", icon: <DollarSign className="h-4 w-4" /> },
    { id: "interest", label: "Interest Income", icon: <TrendingUp className="h-4 w-4" /> },
    { id: "approval", label: "Approval Audit", icon: <CheckCircle2 className="h-4 w-4" /> },
    { id: "collection", label: "Collection Performance", icon: <TrendingUp className="h-4 w-4" /> },
    { id: "statement", label: "Member Statement", icon: <FileText className="h-4 w-4" /> },
  ];

  const handleExport = () => {
    toast.info("Export feature coming soon");
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="sm:px-4 px-3 pt-3 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold">Loan Reports</h1>
          <p className="text-sm text-gray-500 mt-1">Comprehensive loan analytics and reports</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handlePrint}>
            <Printer className="h-3.5 w-3.5 mr-1.5" />
            Print
          </Button>
          <Button variant="outline" size="sm" onClick={handleExport}>
            <Download className="h-3.5 w-3.5 mr-1.5" />
            Export
          </Button>
        </div>
      </div>

      {/* Report Tabs */}
      <Tabs value={activeReport} onValueChange={setActiveReport}>
        <TabsList className="flex flex-wrap h-auto">
          {reports.map((r) => (
            <TabsTrigger key={r.id} value={r.id} className="flex items-center gap-1.5">
              {r.icon}
              {r.label}
            </TabsTrigger>
          ))}
        </TabsList>

        {/* Portfolio Report */}
        <TabsContent value="portfolio">
          {loadingPortfolio ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="h-8 w-8 animate-spin text-green-600" />
            </div>
          ) : portfolioData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card>
                  <CardContent className="p-4">
                    <span className="text-xs text-gray-500">Total Loans</span>
                    <p className="text-xl font-bold">{portfolioData.summary.totalLoans}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <span className="text-xs text-gray-500">Total Amount</span>
                    <p className="text-xl font-bold text-blue-700">{formatCurrency(portfolioData.summary.totalAmount)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <span className="text-xs text-gray-500">Outstanding</span>
                    <p className="text-xl font-bold text-amber-700">{formatCurrency(portfolioData.summary.totalOutstanding)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <span className="text-xs text-gray-500">Collection Rate</span>
                    <p className="text-xl font-bold text-green-700">{portfolioData.summary.collectionRate}%</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold">By Status</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-3 md:grid-cols-6 gap-3">
                    {Object.entries(portfolioData.byStatus).map(([status, count]) => (
                      <div key={status} className="text-center p-3 bg-gray-50 rounded-lg">
                        <p className="text-2xl font-bold">{count as number}</p>
                        <p className="text-xs text-gray-500 capitalize">{status.replace("_", " ")}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-semibold">Loan Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Loan Code</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Member</th>
                          <th className="text-right py-2 px-3 font-medium text-gray-600">Amount</th>
                          <th className="text-right py-2 px-3 font-medium text-gray-600">Outstanding</th>
                          <th className="text-right py-2 px-3 font-medium text-gray-600">Repaid</th>
                          <th className="text-center py-2 px-3 font-medium text-gray-600">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {portfolioData.loans?.slice(0, 50).map((loan: any) => (
                          <tr key={loan.id} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-3 font-mono text-xs">{loan.loanCode || `LN-${String(loan.id).padStart(4, "0")}`}</td>
                            <td className="py-2 px-3">{loan.memberName || "N/A"}</td>
                            <td className="py-2 px-3 text-right">{formatCurrency(loan.amount)}</td>
                            <td className="py-2 px-3 text-right text-amber-700">{formatCurrency(loan.dueAmount)}</td>
                            <td className="py-2 px-3 text-right text-green-700">{formatCurrency(loan.totalPaid)}</td>
                            <td className="py-2 px-3 text-center">
                              <Badge className="text-[10px]" variant={loan.status === "active" ? "default" : "secondary"}>
                                {loan.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : null}
        </TabsContent>

        {/* Monthly Repayments Report */}
        <TabsContent value="repayments">
          <div className="flex items-center gap-3 mb-4">
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 12 }, (_, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>
                    {format(new Date(2024, i, 1), "MMMM")}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-24">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[2023, 2024, 2025, 2026].map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {loadingRepayment ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="h-8 w-8 animate-spin text-green-600" />
            </div>
          ) : repaymentData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card>
                  <CardContent className="p-4">
                    <span className="text-xs text-gray-500">Total Expected</span>
                    <p className="text-xl font-bold">{formatCurrency(repaymentData.summary.totalExpected)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <span className="text-xs text-gray-500">Total Collected</span>
                    <p className="text-xl font-bold text-green-700">{formatCurrency(repaymentData.summary.totalPaid)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <span className="text-xs text-gray-500">Collection Rate</span>
                    <p className="text-xl font-bold text-[#009A44]">{repaymentData.summary.collectionRate}%</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <span className="text-xs text-gray-500">Overdue</span>
                    <p className="text-xl font-bold text-red-700">{repaymentData.summary.overdueCount}</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Loan Code</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Member</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Due Date</th>
                          <th className="text-right py-2 px-3 font-medium text-gray-600">Expected</th>
                          <th className="text-right py-2 px-3 font-medium text-gray-600">Paid</th>
                          <th className="text-center py-2 px-3 font-medium text-gray-600">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {repaymentData.schedules?.map((s: any, i: number) => (
                          <tr key={i} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-3 font-mono text-xs">{s.loanCode || "N/A"}</td>
                            <td className="py-2 px-3">{s.memberName || "N/A"}</td>
                            <td className="py-2 px-3">{s.dueDate ? format(new Date(s.dueDate), "dd MMM yyyy") : "N/A"}</td>
                            <td className="py-2 px-3 text-right">{formatCurrency(s.expectedAmount)}</td>
                            <td className="py-2 px-3 text-right text-green-700">{formatCurrency(s.paidAmount)}</td>
                            <td className="py-2 px-3 text-center">
                              <Badge className={cn("text-[10px]", s.status === "paid" ? "bg-green-100 text-green-700" : s.status === "pending" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700")}>
                                {s.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : null}
        </TabsContent>

        {/* Overdue Report */}
        <TabsContent value="overdue">
          {loadingOverdue ? (
            <div className="flex items-center justify-center py-12">
              <Loader className="h-8 w-8 animate-spin text-green-600" />
            </div>
          ) : overdueData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-3">
                <Card>
                  <CardContent className="p-4">
                    <span className="text-xs text-gray-500">Total Overdue</span>
                    <p className="text-xl font-bold text-red-700">{overdueData.summary.totalOverdue}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <span className="text-xs text-gray-500">Overdue Amount</span>
                    <p className="text-xl font-bold text-red-700">{formatCurrency(overdueData.summary.totalOverdueAmount)}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-4">
                    <span className="text-xs text-gray-500">Total Penalties</span>
                    <p className="text-xl font-bold text-red-600">{formatCurrency(overdueData.summary.totalPenalty)}</p>
                  </CardContent>
                </Card>
              </div>

              <Card>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-gray-50">
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Loan Code</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Member</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Phone</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Due Date</th>
                          <th className="text-right py-2 px-3 font-medium text-gray-600">Outstanding</th>
                          <th className="text-center py-2 px-3 font-medium text-gray-600">Days Overdue</th>
                          <th className="text-right py-2 px-3 font-medium text-gray-600">Penalty</th>
                        </tr>
                      </thead>
                      <tbody>
                        {overdueData.schedules?.map((s: any, i: number) => (
                          <tr key={i} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-3 font-mono text-xs">{s.loanCode || "N/A"}</td>
                            <td className="py-2 px-3">{s.memberName || "N/A"}</td>
                            <td className="py-2 px-3">{s.phone || "N/A"}</td>
                            <td className="py-2 px-3">{s.dueDate ? format(new Date(s.dueDate), "dd MMM yyyy") : "N/A"}</td>
                            <td className="py-2 px-3 text-right text-red-700 font-medium">{formatCurrency(s.outstandingAmount)}</td>
                            <td className="py-2 px-3 text-center">
                              <Badge variant="destructive" className="text-[10px]">{s.daysOverdue}d</Badge>
                            </td>
                            <td className="py-2 px-3 text-right">{formatCurrency(s.penaltyAmount)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : null}
        </TabsContent>

        {/* Disbursement Report */}
        <TabsContent value="disbursement">
          <div className="flex items-center gap-3 mb-4">
            <Select value={String(month)} onValueChange={(v) => setMonth(Number(v))}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Array.from({ length: 12 }, (_, i) => (
                  <SelectItem key={i + 1} value={String(i + 1)}>{format(new Date(2024, i, 1), "MMMM")}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
              <SelectTrigger className="w-24"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[2023, 2024, 2025, 2026].map((y) => (
                  <SelectItem key={y} value={String(y)}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {loadingDisbursement ? (
            <div className="flex items-center justify-center py-12"><Loader className="h-8 w-8 animate-spin text-green-600" /></div>
          ) : disbursementData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card><CardContent className="p-4"><span className="text-xs text-gray-500">Loans Disbursed</span><p className="text-xl font-bold">{disbursementData.summary.totalDisbursed}</p></CardContent></Card>
                <Card><CardContent className="p-4"><span className="text-xs text-gray-500">Total Amount</span><p className="text-xl font-bold text-blue-700">{formatCurrency(disbursementData.summary.totalAmount)}</p></CardContent></Card>
                <Card><CardContent className="p-4"><span className="text-xs text-gray-500">Upfront Interest</span><p className="text-xl font-bold text-amber-700">{formatCurrency(disbursementData.summary.totalUpfrontInterest)}</p></CardContent></Card>
                <Card><CardContent className="p-4"><span className="text-xs text-gray-500">Average Amount</span><p className="text-xl font-bold">{formatCurrency(disbursementData.summary.averageAmount)}</p></CardContent></Card>
              </div>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Disbursement Details</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b bg-gray-50">
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Loan Code</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Member</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-600">Amount</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-600">Disbursed</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-600">Upfront Interest</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Date</th>
                        <th className="text-center py-2 px-3 font-medium text-gray-600">Status</th>
                      </tr></thead>
                      <tbody>
                        {disbursementData.disbursements?.map((d: any) => (
                          <tr key={d.id} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-3 font-mono text-xs">{d.loanCode}</td>
                            <td className="py-2 px-3">{d.memberName}</td>
                            <td className="py-2 px-3 text-right">{formatCurrency(d.amount)}</td>
                            <td className="py-2 px-3 text-right text-green-700">{formatCurrency(d.disbursedAmount)}</td>
                            <td className="py-2 px-3 text-right text-amber-700">{formatCurrency(d.upfrontInterest)}</td>
                            <td className="py-2 px-3">{d.disbursementDate ? format(new Date(d.disbursementDate), "dd MMM yyyy") : "N/A"}</td>
                            <td className="py-2 px-3 text-center"><Badge className="text-[10px]" variant={d.status === "active" ? "default" : "secondary"}>{d.status}</Badge></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : null}
        </TabsContent>

        {/* Interest Income Report */}
        <TabsContent value="interest">
          {loadingInterest ? (
            <div className="flex items-center justify-center py-12"><Loader className="h-8 w-8 animate-spin text-green-600" /></div>
          ) : interestData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card><CardContent className="p-4"><span className="text-xs text-gray-500">Upfront Interest Earned</span><p className="text-xl font-bold text-green-700">{formatCurrency(interestData.summary.totalUpfrontInterest)}</p></CardContent></Card>
                <Card><CardContent className="p-4"><span className="text-xs text-gray-500">Expected Interest</span><p className="text-xl font-bold">{formatCurrency(interestData.summary.expectedInterest)}</p></CardContent></Card>
                <Card><CardContent className="p-4"><span className="text-xs text-gray-500">Collected Interest</span><p className="text-xl font-bold text-blue-700">{formatCurrency(interestData.summary.collectedInterest)}</p></CardContent></Card>
                <Card><CardContent className="p-4"><span className="text-xs text-gray-500">Collection Rate</span><p className="text-xl font-bold text-[#009A44]">{interestData.summary.collectionRate}%</p></CardContent></Card>
              </div>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Monthly Interest Trend</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b bg-gray-50">
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Month</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-600">Interest Earned</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-600">Loans</th>
                      </tr></thead>
                      <tbody>
                        {interestData.monthlyTrend?.map((m: any, i: number) => (
                          <tr key={i} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-3 font-medium">{m.month}</td>
                            <td className="py-2 px-3 text-right text-green-700">{formatCurrency(m.interestEarned)}</td>
                            <td className="py-2 px-3 text-right">{m.loans}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
              {interestData.byLoanType && Object.keys(interestData.byLoanType).length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">By Loan Type</CardTitle></CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {Object.entries(interestData.byLoanType).map(([type, data]: [string, any]) => (
                        <div key={type} className="p-3 bg-gray-50 rounded-lg">
                          <p className="text-sm font-medium">{type}</p>
                          <p className="text-lg font-bold text-green-700">{formatCurrency(data.interest)}</p>
                          <p className="text-xs text-gray-500">{data.count} loans</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : null}
        </TabsContent>

        {/* Approval Audit Report */}
        <TabsContent value="approval">
          {loadingApproval ? (
            <div className="flex items-center justify-center py-12"><Loader className="h-8 w-8 animate-spin text-green-600" /></div>
          ) : approvalData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Card><CardContent className="p-4"><span className="text-xs text-gray-500">Total Applications</span><p className="text-xl font-bold">{approvalData.summary.totalApplications}</p></CardContent></Card>
                <Card><CardContent className="p-4"><span className="text-xs text-gray-500">Approved</span><p className="text-xl font-bold text-green-700">{approvalData.summary.approvedCount}</p></CardContent></Card>
                <Card><CardContent className="p-4"><span className="text-xs text-gray-500">Rejected</span><p className="text-xl font-bold text-red-700">{approvalData.summary.rejectedCount}</p></CardContent></Card>
                <Card><CardContent className="p-4"><span className="text-xs text-gray-500">Pending</span><p className="text-xl font-bold text-amber-700">{approvalData.summary.pendingCount}</p></CardContent></Card>
                <Card><CardContent className="p-4"><span className="text-xs text-gray-500">Avg Approval Time</span><p className="text-xl font-bold">{approvalData.summary.avgApprovalTimeHours}h</p></CardContent></Card>
              </div>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Approval History</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b bg-gray-50">
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Loan Code</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Member</th>
                        <th className="text-center py-2 px-3 font-medium text-gray-600">Approved By</th>
                        <th className="text-center py-2 px-3 font-medium text-gray-600">Time (hrs)</th>
                        <th className="text-center py-2 px-3 font-medium text-gray-600">Status</th>
                      </tr></thead>
                      <tbody>
                        {approvalData.history?.slice(0, 50).map((h: any) => (
                          <tr key={h.loanId} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-3 font-mono text-xs">{h.loanCode}</td>
                            <td className="py-2 px-3">{h.memberName}</td>
                            <td className="py-2 px-3 text-center">
                              <div className="flex items-center justify-center gap-1">
                                {h.presidentApproved && <Badge className="text-[10px] bg-green-100 text-green-700">Pres</Badge>}
                                {h.secretaryApproved && <Badge className="text-[10px] bg-green-100 text-green-700">Sec</Badge>}
                                {h.accountantApproved && <Badge className="text-[10px] bg-green-100 text-green-700">Acc</Badge>}
                              </div>
                            </td>
                            <td className="py-2 px-3 text-center">{h.approvalTimeHours ?? "N/A"}</td>
                            <td className="py-2 px-3 text-center">
                              <Badge className={cn("text-[10px]", h.status === "completed" || h.status === "active" ? "bg-green-100 text-green-700" : h.status === "rejected" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700")}>
                                {h.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : null}
        </TabsContent>

        {/* Collection Performance Report */}
        <TabsContent value="collection">
          {loadingCollection ? (
            <div className="flex items-center justify-center py-12"><Loader className="h-8 w-8 animate-spin text-green-600" /></div>
          ) : collectionData ? (
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <Card><CardContent className="p-4"><span className="text-xs text-gray-500">Total Expected</span><p className="text-xl font-bold">{formatCurrency(collectionData.summary.totalExpected)}</p></CardContent></Card>
                <Card><CardContent className="p-4"><span className="text-xs text-gray-500">Total Collected</span><p className="text-xl font-bold text-green-700">{formatCurrency(collectionData.summary.totalCollected)}</p></CardContent></Card>
                <Card><CardContent className="p-4"><span className="text-xs text-gray-500">Collection Rate</span><p className="text-xl font-bold text-[#009A44]">{collectionData.summary.overallCollectionRate}%</p></CardContent></Card>
                <Card><CardContent className="p-4"><span className="text-xs text-gray-500">Overdue Installments</span><p className="text-xl font-bold text-red-700">{collectionData.summary.totalOverdueInstallments}</p></CardContent></Card>
              </div>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Monthly Performance</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b bg-gray-50">
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Month</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-600">Expected</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-600">Collected</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-600">Rate</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-600">Overdue</th>
                      </tr></thead>
                      <tbody>
                        {collectionData.monthlyPerformance?.map((m: any, i: number) => (
                          <tr key={i} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-3 font-medium">{m.month}</td>
                            <td className="py-2 px-3 text-right">{formatCurrency(m.expected)}</td>
                            <td className="py-2 px-3 text-right text-green-700">{formatCurrency(m.collected)}</td>
                            <td className="py-2 px-3 text-right">
                              <span className={cn("font-medium", m.collectionRate >= 80 ? "text-green-700" : m.collectionRate >= 50 ? "text-amber-700" : "text-red-700")}>
                                {m.collectionRate}%
                              </span>
                            </td>
                            <td className="py-2 px-3 text-right text-red-700">{m.overdueCount}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : null}
        </TabsContent>

        {/* Member Statement Report */}
        <TabsContent value="statement">
          <div className="mb-4">
            <Input
              placeholder="Search by name, teacher ID, email, or phone..."
              value={memberSearch}
              onChange={(e) => setMemberSearch(e.target.value)}
              className="max-w-md"
            />
          </div>
          {loadingStatement ? (
            <div className="flex items-center justify-center py-12"><Loader className="h-8 w-8 animate-spin text-green-600" /></div>
          ) : statementData ? (
            <div className="space-y-4">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Member Information</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                    <div><span className="text-xs text-gray-500">Name</span><p className="font-medium">{statementData.member?.name}</p></div>
                    <div><span className="text-xs text-gray-500">Teacher ID</span><p className="font-medium">{statementData.member?.teacherId}</p></div>
                    <div><span className="text-xs text-gray-500">Phone</span><p className="font-medium">{statementData.member?.phone || "N/A"}</p></div>
                    <div><span className="text-xs text-gray-500">Email</span><p className="font-medium">{statementData.member?.email || "N/A"}</p></div>
                    <div><span className="text-xs text-gray-500">Group</span><p className="font-medium">{statementData.member?.groupName || "N/A"}</p></div>
                  </div>
                </CardContent>
              </Card>
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <Card><CardContent className="p-4"><span className="text-xs text-gray-500">Total Loans</span><p className="text-xl font-bold">{statementData.summary?.totalLoans}</p></CardContent></Card>
                <Card><CardContent className="p-4"><span className="text-xs text-gray-500">Total Borrowed</span><p className="text-xl font-bold text-blue-700">{formatCurrency(statementData.summary?.totalBorrowed)}</p></CardContent></Card>
                <Card><CardContent className="p-4"><span className="text-xs text-gray-500">Total Paid</span><p className="text-xl font-bold text-green-700">{formatCurrency(statementData.summary?.totalPaid)}</p></CardContent></Card>
                <Card><CardContent className="p-4"><span className="text-xs text-gray-500">Outstanding</span><p className="text-xl font-bold text-amber-700">{formatCurrency(statementData.summary?.totalOutstanding)}</p></CardContent></Card>
                <Card><CardContent className="p-4"><span className="text-xs text-gray-500">Total Interest</span><p className="text-xl font-bold">{formatCurrency(statementData.summary?.totalInterest)}</p></CardContent></Card>
              </div>
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Loan History</CardTitle></CardHeader>
                <CardContent>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b bg-gray-50">
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Loan Code</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-600">Amount</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-600">Paid</th>
                        <th className="text-right py-2 px-3 font-medium text-gray-600">Outstanding</th>
                        <th className="text-left py-2 px-3 font-medium text-gray-600">Disbursed</th>
                        <th className="text-center py-2 px-3 font-medium text-gray-600">Status</th>
                      </tr></thead>
                      <tbody>
                        {statementData.loans?.map((l: any) => (
                          <tr key={l.id} className="border-b hover:bg-gray-50">
                            <td className="py-2 px-3 font-mono text-xs">{l.loanCode}</td>
                            <td className="py-2 px-3 text-right">{formatCurrency(l.amount)}</td>
                            <td className="py-2 px-3 text-right text-green-700">{formatCurrency(l.totalPaid)}</td>
                            <td className="py-2 px-3 text-right text-amber-700">{formatCurrency(l.dueAmount)}</td>
                            <td className="py-2 px-3">{l.disbursementDate ? format(new Date(l.disbursementDate), "dd MMM yyyy") : "N/A"}</td>
                            <td className="py-2 px-3 text-center">
                              <Badge className={cn("text-[10px]", ["active", "disbursed"].includes(l.status) ? "bg-green-100 text-green-700" : ["completed", "fully_repaid"].includes(l.status) ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-700")}>
                                {l.status}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
              {statementData.schedules && statementData.schedules.length > 0 && (
                <Card>
                  <CardHeader className="pb-2"><CardTitle className="text-sm font-semibold">Repayment Schedule</CardTitle></CardHeader>
                  <CardContent>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead><tr className="border-b bg-gray-50">
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Loan</th>
                          <th className="text-center py-2 px-3 font-medium text-gray-600">#</th>
                          <th className="text-right py-2 px-3 font-medium text-gray-600">Amount</th>
                          <th className="text-right py-2 px-3 font-medium text-gray-600">Paid</th>
                          <th className="text-left py-2 px-3 font-medium text-gray-600">Due Date</th>
                          <th className="text-center py-2 px-3 font-medium text-gray-600">Status</th>
                        </tr></thead>
                        <tbody>
                          {statementData.schedules.slice(0, 100).map((s: any) => (
                            <tr key={s.id} className="border-b hover:bg-gray-50">
                              <td className="py-2 px-3 font-mono text-xs">{s.loanCode || "N/A"}</td>
                              <td className="py-2 px-3 text-center">{s.installmentNumber}</td>
                              <td className="py-2 px-3 text-right">{formatCurrency(s.totalAmount)}</td>
                              <td className="py-2 px-3 text-right text-green-700">{formatCurrency(s.paidAmount)}</td>
                              <td className="py-2 px-3">{s.dueDate ? format(new Date(s.dueDate), "dd MMM yyyy") : "N/A"}</td>
                              <td className="py-2 px-3 text-center">
                                <Badge className={cn("text-[10px]", s.status === "paid" ? "bg-green-100 text-green-700" : s.status === "partial" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700")}>
                                  {s.status}
                                </Badge>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          ) : memberSearch ? (
            <Card><CardContent className="py-12 text-center"><p className="text-gray-500">No results found for "{memberSearch}"</p></CardContent></Card>
          ) : (
            <Card><CardContent className="py-12 text-center"><FileText className="h-12 w-12 text-gray-300 mx-auto mb-3" /><p className="text-gray-500">Enter a member name or ID to view their statement</p></CardContent></Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
